import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const StatsSection = () => {
  const stats = [
    { number: '500+', label: 'Live Webinars', icon: 'Video', color: 'blue' },
    { number: '50K+', label: 'Active Attendees', icon: 'Users', color: 'indigo' },
    { number: '89%', label: 'Engagement Rate', icon: 'TrendingUp', color: 'blue' },
    { number: '99.9%', label: 'Platform Uptime', icon: 'Zap', color: 'indigo' }
  ];

  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full mb-4">
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
            <span className="text-sm font-bold text-blue-900">Real-Time Stats</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
            Numbers That Speak
            <span className="block text-blue-600">For Themselves</span>
          </h2>
        </motion.div>

        {/* Timeline Stats */}
        <div className="relative">
          {/* Connecting Line */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-indigo-200 to-blue-200 origin-left hidden lg:block"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
                className="relative"
              >
                {/* Icon Circle */}
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 360 }}
                  transition={{ duration: 0.6 }}
                  className={`w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-${stat.color}-500 to-${stat.color}-700 rounded-full flex items-center justify-center shadow-xl relative z-10`}
                >
                  <Icon name={stat.icon} size={36} className="text-white" />
                  
                  {/* Pulse Ring */}
                  <motion.div
                    animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className={`absolute inset-0 rounded-full border-4 border-${stat.color}-400`}
                  />
                </motion.div>

                {/* Number & Label */}
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.2 + 0.3, type: "spring" }}
                  className="text-center"
                >
                  <div className="text-5xl md:text-6xl font-black text-gray-900 mb-2">
                    {stat.number}
                  </div>
                  <div className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
                    {stat.label}
                  </div>
                </motion.div>

                {/* Decorative Arrow (except last) */}
                {idx < stats.length - 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.2 + 0.5 }}
                    className="hidden lg:block absolute top-10 -right-12 text-blue-300"
                  >
                    <Icon name="ArrowRight" size={24} />
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom Feature Cards */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="mt-20 grid md:grid-cols-3 gap-6"
        >
          {[
            { icon: 'Shield', title: 'Enterprise Security', desc: 'Bank-grade encryption' },
            { icon: 'Globe', title: 'Global Coverage', desc: '200+ countries served' },
            { icon: 'Award', title: 'Industry Leader', desc: '#1 rated platform' }
          ].map((item, idx) => (
            <div
              key={idx}
              className="flex items-center gap-4 p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 hover:shadow-lg transition-shadow"
            >
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <Icon name={item.icon} size={24} className="text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default StatsSection;
